import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X, Upload, Users, Repeat, Clock, Search, AlertTriangle, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendar = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [markets, setMarkets] = useState([]);
  
  // Filtering state
  const [filterView, setFilterView] = useState('all'); // all, my-events, team, department, market
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('');
  const [selectedMarketFilter, setSelectedMarketFilter] = useState('');
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');
  const [selectedUserFilter, setSelectedUserFilter] = useState('');
  
  // Search & Quick Add state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddText, setQuickAddText] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    all_day: false,
    location: '',
    event_type: 'other',
    priority: 'medium',
    color: '#3b82f6',
    is_recurring: false,
    recurrence_rule: 'daily',
    recurrence_end_date: '',
    attendees: [],
    shared_with_users: [],
    shared_with_teams: [],
    is_public: false,
    attachments: []
  });

  useEffect(() => {
    fetchEvents();
    fetchUsers();
    fetchTeams();
    fetchMarkets();
  }, [currentDate, view, filterView, selectedTeamFilter, selectedMarketFilter, selectedDepartmentFilter, selectedUserFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const params = {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        view,
        filter_view: filterView
      };
      
      if (selectedTeamFilter) params.team_id = selectedTeamFilter;
      if (selectedMarketFilter) params.market_id = selectedMarketFilter;
      if (selectedDepartmentFilter) params.department = selectedDepartmentFilter;
      if (selectedUserFilter) params.user_id_filter = selectedUserFilter;
      
      const response = await axios.get(`${API}/calendar/events`, { params });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setAllTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchMarkets = async () => {
    try {
      const response = await axios.get(`${API}/markets`);
      setMarkets(response.data);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const isManager = () => {
    return user && ['manager', 'general_manager'].includes(user.role);
  };

  const isSuperAdmin = () => {
    return user && ['super_admin', 'ceo', 'president'].includes(user.role);
  };

  const getUserColorForEvent = (event) => {
    // For managers viewing team events, color-code by creator
    if (isManager() && event.created_by) {
      const creator = allUsers.find(u => u.id === event.created_by);
      if (creator) {
        // Generate consistent color based on user ID
        const hash = creator.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const colors = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'];
        return colors[hash % colors.length];
      }
    }
    return event.color || '#3b82f6';
  };


  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.get(`${API}/calendar/search`, {
        params: { q: searchQuery }
      });
      setSearchResults(response.data);
      setShowSearchDialog(true);
    } catch (error) {
      console.error('Error searching events:', error);
      toast.error('Search failed');
    }
  };

  const checkConflicts = async (startDate, endDate, eventId = null) => {
    try {
      const response = await axios.get(`${API}/calendar/conflicts`, {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString(),
          event_id: eventId
        }
      });
      
      if (response.data.has_conflicts) {
        setConflicts(response.data.conflicts);
        setShowConflictWarning(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return false;
    }
  };

  const handleQuickAdd = async () => {
    if (!quickAddText.trim()) return;
    
    try {
      // Parse natural language (simple version)
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Default: tomorrow at 10am for 1 hour
      const startDate = new Date(tomorrow);
      startDate.setHours(10, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setHours(11, 0, 0, 0);
      
      await axios.post(`${API}/calendar/events`, {
        title: quickAddText,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        event_type: 'other',
        priority: 'medium',
        color: '#3b82f6',
        all_day: false
      });
      
      toast.success('Event created quickly!');
      setShowQuickAdd(false);
      setQuickAddText('');
      fetchEvents();
    } catch (error) {
      console.error('Error creating quick event:', error);
      toast.error('Failed to create event');
    }
  };


  const getDateRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (view === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    } else if (view === 'week') {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end.setDate(end.getDate() + (6 - day));
    } else {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (7 * direction));
    } else {
      newDate.setDate(newDate.getDate() + direction);
    }
    
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    // Check for conflicts
    const hasConflicts = await checkConflicts(eventForm.start_date, eventForm.end_date);
    if (hasConflicts && !window.confirm('You have conflicting events. Do you want to create this event anyway?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/calendar/events`, {
        ...eventForm,
        start_date: new Date(eventForm.start_date).toISOString(),
        end_date: new Date(eventForm.end_date).toISOString(),
        recurrence_end_date: eventForm.recurrence_end_date ? new Date(eventForm.recurrence_end_date).toISOString() : null
      });
      toast.success('Event created successfully');
      setShowEventDialog(false);
      resetEventForm();
      setConflicts([]);
      setShowConflictWarning(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/calendar/events/${selectedEvent.id}`, {
        ...eventForm,
        start_date: new Date(eventForm.start_date).toISOString(),
        end_date: new Date(eventForm.end_date).toISOString(),
        recurrence_end_date: eventForm.recurrence_end_date ? new Date(eventForm.recurrence_end_date).toISOString() : null
      });
      toast.success('Event updated successfully');
      setShowEventDialog(false);
      setSelectedEvent(null);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/calendar/events/${selectedEvent.id}`);
      toast.success('Event deleted successfully');
      setShowEventDialog(false);
      setSelectedEvent(null);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    if (!draggedEvent) return;

    try {
      const duration = new Date(draggedEvent.end_date) - new Date(draggedEvent.start_date);
      const newStart = new Date(targetDate);
      const newEnd = new Date(newStart.getTime() + duration);

      await axios.patch(`${API}/calendar/events/${draggedEvent.id}/reschedule`, {
        start_date: newStart.toISOString(),
        end_date: newEnd.toISOString()
      });

      toast.success('Event rescheduled');
      fetchEvents();
    } catch (error) {
      console.error('Error rescheduling event:', error);
      toast.error('Failed to reschedule event');
    } finally {
      setDraggedEvent(null);
    }
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      all_day: false,
      location: '',
      event_type: 'other',
      priority: 'medium',
      color: '#3b82f6',
      is_recurring: false,
      recurrence_rule: 'daily',
      recurrence_end_date: '',
      attendees: [],
      shared_with_users: [],
      shared_with_teams: [],
      is_public: false,
      attachments: []
    });
  };

  const openEventDialog = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setEventForm({
        title: event.title,
        description: event.description || '',
        start_date: new Date(event.start_date).toISOString().slice(0, 16),
        end_date: new Date(event.end_date).toISOString().slice(0, 16),
        all_day: event.all_day,
        location: event.location || '',
        event_type: event.event_type,
        priority: event.priority,
        color: event.color || '#3b82f6',
        is_recurring: event.is_recurring || false,
        recurrence_rule: event.recurrence_rule || 'daily',
        recurrence_end_date: event.recurrence_end_date ? new Date(event.recurrence_end_date).toISOString().slice(0, 10) : '',
        attendees: event.attendees || [],
        shared_with_users: event.shared_with_users || [],
        shared_with_teams: event.shared_with_teams || [],
        is_public: event.is_public || false,
        attachments: event.attachments || []
      });
    } else {
      setSelectedEvent(null);
      resetEventForm();
    }
    setShowEventDialog(true);
  };

  const formatDateHeader = () => {
    const options = view === 'month' 
      ? { month: 'long', year: 'numeric' }
      : view === 'week'
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    
    return currentDate.toLocaleDateString('en-US', options);
  };

  const toggleAttendee = (userId) => {
    setEventForm(prev => ({
      ...prev,
      attendees: prev.attendees.includes(userId)
        ? prev.attendees.filter(id => id !== userId)
        : [...prev.attendees, userId]
    }));
  };

  const toggleSharedUser = (userId) => {
    setEventForm(prev => ({
      ...prev,
      shared_with_users: prev.shared_with_users.includes(userId)
        ? prev.shared_with_users.filter(id => id !== userId)
        : [...prev.shared_with_users, userId]
    }));
  };

  const toggleSharedTeam = (teamId) => {
    setEventForm(prev => ({
      ...prev,
      shared_with_teams: prev.shared_with_teams.includes(teamId)
        ? prev.shared_with_teams.filter(id => id !== teamId)
        : [...prev.shared_with_teams, teamId]
    }));
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-24 bg-muted/30 border border-border" />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dayEvents = events.filter(e => {
        const eventStart = new Date(e.start_date).toISOString().split('T')[0];
        return eventStart === dateStr;
      });
      
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      
      days.push(
        <div
          key={day}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, date)}
          className={`min-h-24 border border-border p-2 ${isToday ? 'bg-primary/5 ring-2 ring-primary' : 'bg-card'}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : 'text-foreground'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayEvents.slice(0, 3).map(event => (
              <button
                key={event.id}
                draggable
                onDragStart={(e) => handleDragStart(e, event)}
                onClick={() => openEventDialog(event)}
                className="w-full text-left text-xs px-1.5 py-0.5 rounded truncate hover:opacity-80 transition-opacity cursor-move"
                style={{ backgroundColor: getUserColorForEvent(event), color: 'white' }}
              >
                {event.title}
              </button>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-muted-foreground pl-1.5">
                +{dayEvents.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-7 gap-0 border-t border-l border-border">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-muted p-2 text-center text-sm font-semibold border-r border-b border-border">
            {day}
          </div>
        ))}
        {days}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = new Date(currentDate);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - day);
    
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      return date;
    });

    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-8 border-b border-border">
          <div className="p-2 text-sm font-semibold bg-muted">Time</div>
          {weekDays.map((date, i) => {
            const isToday = date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0];
            return (
              <div key={i} className={`p-2 text-center text-sm font-semibold ${isToday ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>
                <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className="text-xs font-normal">{date.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8 border-b border-border" style={{ minHeight: '60px' }}>
              <div className="p-2 text-xs text-muted-foreground bg-muted/30">
                {hour.toString().padStart(2, '0')}:00
              </div>
              {weekDays.map((date, i) => {
                const cellDate = new Date(date);
                cellDate.setHours(hour, 0, 0, 0);
                const cellEvents = events.filter(e => {
                  const eventStart = new Date(e.start_date);
                  return eventStart.getDate() === date.getDate() &&
                         eventStart.getMonth() === date.getMonth() &&
                         eventStart.getHours() === hour;
                });

                return (
                  <div
                    key={i}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, cellDate)}
                    className="border-r border-border p-1 hover:bg-accent/50 transition-colors"
                  >
                    {cellEvents.map(event => (
                      <button
                        key={event.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, event)}
                        onClick={() => openEventDialog(event)}
                        className="w-full text-left text-xs px-1 py-0.5 rounded truncate mb-1 cursor-move"
                        style={{ backgroundColor: getUserColorForEvent(event), color: 'white' }}
                      >
                        {event.title}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayStr = currentDate.toISOString().split('T')[0];

    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="p-4 bg-muted border-b border-border">
          <h2 className="text-lg font-semibold">
            {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </h2>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: '700px' }}>
          {hours.map(hour => {
            const cellDate = new Date(currentDate);
            cellDate.setHours(hour, 0, 0, 0);
            const hourEvents = events.filter(e => {
              const eventStart = new Date(e.start_date);
              return eventStart.toISOString().split('T')[0] === dayStr &&
                     eventStart.getHours() === hour;
            });

            return (
              <div key={hour} className="grid grid-cols-12 border-b border-border" style={{ minHeight: '80px' }}>
                <div className="col-span-2 p-3 text-sm text-muted-foreground bg-muted/30 border-r border-border">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                <div
                  className="col-span-10 p-2 hover:bg-accent/50 transition-colors"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, cellDate)}
                >
                  {hourEvents.map(event => (
                    <button
                      key={event.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, event)}
                      onClick={() => openEventDialog(event)}
                      className="w-full text-left px-3 py-2 rounded mb-2 cursor-move hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: getUserColorForEvent(event), color: 'white' }}
                    >
                      <div className="font-medium">{event.title}</div>
                      {event.location && <div className="text-xs opacity-90">{event.location}</div>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Layout>
    <div className="p-4 space-y-4 bg-background min-h-screen">
      {/* Header - Compact */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <CalendarIcon className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Calendar</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Search */}
          <div className="flex gap-1">
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-48"
            />
            <Button variant="outline" size="icon" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Quick Add */}
          <Button variant="outline" size="sm" onClick={() => setShowQuickAdd(true)}>
            <Zap className="h-4 w-4 mr-1" />
            Quick
          </Button>
          
          {/* New Event */}
          <Button size="sm" onClick={() => openEventDialog()}>
            <Plus className="h-4 w-4 mr-1" />
            Event
          </Button>
        </div>
      </div>

      {/* Controls - Single Row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 text-lg font-semibold text-foreground">{formatDateHeader()}</span>
        </div>
        
        <div className="flex gap-1">
          <Button
            variant={view === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('month')}
          >
            Month
          </Button>
          <Button
            variant={view === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('week')}
          >
            Week
          </Button>
          <Button
            variant={view === 'day' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('day')}
          >
            Day
          </Button>
        </div>
      </div>

      {/* Filtering Options - Managers and Admins - Compact */}
      {(isManager() || isSuperAdmin()) && (
        <div className="flex items-center gap-2 flex-wrap p-3 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center gap-1">
            <Label className="text-xs font-medium">View:</Label>
            <Select value={filterView} onValueChange={setFilterView}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="my-events">My Events</SelectItem>
                {isManager() && <SelectItem value="team">Team Events</SelectItem>}
                {isSuperAdmin() && <SelectItem value="department">Department</SelectItem>}
                {(isManager() || isSuperAdmin()) && <SelectItem value="market">Market</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {filterView === 'team' && allTeams.length > 0 && (
            <div className="flex items-center gap-1">
              <Label className="text-xs font-medium">Team:</Label>
              <Select value={selectedTeamFilter} onValueChange={setSelectedTeamFilter}>
                <SelectTrigger className="w-36 h-8 text-xs">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Teams</SelectItem>
                  {allTeams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterView === 'market' && markets.length > 0 && (
            <div className="flex items-center gap-1">
              <Label className="text-xs font-medium">Market:</Label>
              <Select value={selectedMarketFilter} onValueChange={setSelectedMarketFilter}>
                <SelectTrigger className="w-36 h-8 text-xs">
                  <SelectValue placeholder="Select market" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Markets</SelectItem>
                  {markets.map(market => (
                    <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterView === 'department' && isSuperAdmin() && (
            <div className="flex items-center gap-1">
              <Label className="text-xs font-medium">Dept:</Label>
              <Select value={selectedDepartmentFilter} onValueChange={setSelectedDepartmentFilter}>
                <SelectTrigger className="w-36 h-8 text-xs">
                  <SelectValue placeholder="Select dept" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Departments</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Crew">Crew</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isManager() && filterView === 'team' && allUsers.length > 0 && (
            <div className="flex items-center gap-1">
              <Label className="text-xs font-medium">User:</Label>
              <Select value={selectedUserFilter} onValueChange={setSelectedUserFilter}>
                <SelectTrigger className="w-36 h-8 text-xs">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Users</SelectItem>
                  {allUsers.filter(u => user.team_ids?.some(tid => u.team_ids?.includes(tid))).map(usr => (
                    <SelectItem key={usr.id} value={usr.id}>
                      {usr.first_name} {usr.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isManager() && filterView === 'team' && (
            <span className="text-xs text-muted-foreground ml-auto">
              Color = team member
            </span>
          )}
        </div>
      )}

      {/* Calendar Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading events...</div>
      ) : (
        <>
          {view === 'month' && renderMonthView()}
          {view === 'week' && renderWeekView()}
          {view === 'day' && renderDayView()}
        </>
      )}

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>End Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="all_day"
                checked={eventForm.all_day}
                onCheckedChange={(checked) => setEventForm({ ...eventForm, all_day: checked })}
              />
              <Label htmlFor="all_day" className="cursor-pointer">All day event</Label>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Meeting room, address, etc."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={eventForm.event_type} onValueChange={(val) => setEventForm({ ...eventForm, event_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="sales_appointment">Sales Appointment</SelectItem>
                    <SelectItem value="job_site_visit">Job Site Visit</SelectItem>
                    <SelectItem value="crew_schedule">Crew Schedule</SelectItem>
                    <SelectItem value="call_center_shift">Call Center Shift</SelectItem>
                    <SelectItem value="eos_meeting">EOS Meeting</SelectItem>
                    <SelectItem value="task_deadline">Task Deadline</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={eventForm.priority} onValueChange={(val) => setEventForm({ ...eventForm, priority: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Input
                  type="color"
                  value={eventForm.color}
                  onChange={(e) => setEventForm({ ...eventForm, color: e.target.value })}
                  className="h-10"
                />
              </div>
            </div>

            {/* Recurring Events */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                <Checkbox
                  id="is_recurring"
                  checked={eventForm.is_recurring}
                  onCheckedChange={(checked) => setEventForm({ ...eventForm, is_recurring: checked })}
                />
                <Label htmlFor="is_recurring" className="cursor-pointer">Recurring event</Label>
              </div>
              
              {eventForm.is_recurring && (
                <div className="grid grid-cols-2 gap-4 ml-6">
                  <div>
                    <Label>Repeat</Label>
                    <Select value={eventForm.recurrence_rule} onValueChange={(val) => setEventForm({ ...eventForm, recurrence_rule: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={eventForm.recurrence_end_date}
                      onChange={(e) => setEventForm({ ...eventForm, recurrence_end_date: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Attendees */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <Label>Attendees</Label>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-2 ml-6">
                {allUsers.slice(0, 10).map(user => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`attendee-${user.id}`}
                      checked={eventForm.attendees.includes(user.id)}
                      onCheckedChange={() => toggleAttendee(user.id)}
                    />
                    <Label htmlFor={`attendee-${user.id}`} className="cursor-pointer text-sm">
                      {user.first_name} {user.last_name} ({user.email})
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Sharing */}
            <div className="space-y-3 border-t pt-4">
              <Label>Sharing & Visibility</Label>
              
              <div className="flex items-center space-x-2 ml-2">
                <Checkbox
                  id="is_public"
                  checked={eventForm.is_public}
                  onCheckedChange={(checked) => setEventForm({ ...eventForm, is_public: checked })}
                />
                <Label htmlFor="is_public" className="cursor-pointer text-sm">Public (visible to everyone)</Label>
              </div>

              {!eventForm.is_public && (
                <>
                  <div className="ml-2">
                    <Label className="text-sm text-muted-foreground">Share with users</Label>
                    <div className="max-h-24 overflow-y-auto space-y-1 mt-1">
                      {allUsers.slice(0, 8).map(user => (
                        <div key={user.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`share-user-${user.id}`}
                            checked={eventForm.shared_with_users.includes(user.id)}
                            onCheckedChange={() => toggleSharedUser(user.id)}
                          />
                          <Label htmlFor={`share-user-${user.id}`} className="cursor-pointer text-xs">
                            {user.first_name} {user.last_name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="ml-2">
                    <Label className="text-sm text-muted-foreground">Share with teams</Label>
                    <div className="max-h-24 overflow-y-auto space-y-1 mt-1">
                      {allTeams.slice(0, 8).map(team => (
                        <div key={team.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`share-team-${team.id}`}
                            checked={eventForm.shared_with_teams.includes(team.id)}
                            onCheckedChange={() => toggleSharedTeam(team.id)}
                          />
                          <Label htmlFor={`share-team-${team.id}`} className="cursor-pointer text-xs">
                            {team.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between pt-4 border-t">
              {selectedEvent && (
                <Button type="button" variant="destructive" onClick={handleDeleteEvent}>
                  Delete Event
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => setShowEventDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedEvent ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search Results Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Search Results ({searchResults.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {searchResults.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No events found matching "{searchQuery}"
              </div>
            ) : (
              searchResults.map(event => (
                <button
                  key={event.id}
                  onClick={() => {
                    setShowSearchDialog(false);
                    openEventDialog(event);
                  }}
                  className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{event.title}</div>
                      {event.description && (
                        <div className="text-sm text-muted-foreground truncate mt-1">
                          {event.description}
                        </div>
                      )}
                      <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                        <span>{new Date(event.start_date).toLocaleDateString()}</span>
                        <span>{new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {event.location && <span>📍 {event.location}</span>}
                      </div>
                    </div>
                    <Badge
                      style={{ backgroundColor: event.color || '#3b82f6' }}
                      className="text-white"
                    >
                      {event.event_type}
                    </Badge>
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Dialog */}
      <Dialog open={showQuickAdd} onOpenChange={setShowQuickAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quick Add Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Event Title</Label>
              <Input
                placeholder="e.g., Team meeting, Client call..."
                value={quickAddText}
                onChange={(e) => setQuickAddText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickAdd()}
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-2">
                Event will be created for tomorrow at 10:00 AM (1 hour duration)
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowQuickAdd(false)}>
                Cancel
              </Button>
              <Button onClick={handleQuickAdd}>
                <Zap className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conflict Warning Dialog */}
      <Dialog open={showConflictWarning} onOpenChange={setShowConflictWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Schedule Conflict Detected
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You have {conflicts.length} conflicting event(s) at this time:
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {conflicts.map(conflict => (
                <div key={conflict.id} className="p-2 rounded bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
                  <div className="font-medium text-sm">{conflict.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(conflict.start_date).toLocaleString()} - {new Date(conflict.end_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConflictWarning(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default Calendar;
